import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import type { ArticleData, ArticleSection } from '@/mocks/articles';

interface ArticleLayoutProps {
  article: ArticleData;
}

function SectionRenderer({ section }: { section: ArticleSection }) {
  switch (section.type) {
    case 'heading':
      return (
        <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4 mt-10">
          {section.content}
        </h2>
      );

    case 'subheading':
      return (
        <h3 className="text-lg font-heading font-semibold text-foreground-900 mt-6 mb-3">
          {section.content}
        </h3>
      );

    case 'text':
      return (
        <p
          className="text-foreground-700 leading-relaxed mb-4"
          dangerouslySetInnerHTML={{ __html: section.content || '' }}
        />
      );

    case 'list-ordered':
      return (
        <ol className="list-decimal pl-5 space-y-3 text-foreground-700 leading-relaxed mb-4">
          {section.items?.map((item, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
          ))}
        </ol>
      );

    case 'list-unordered':
      return (
        <ul className="list-disc pl-5 space-y-3 text-foreground-700 leading-relaxed mb-4">
          {section.items?.map((item, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
          ))}
        </ul>
      );

    case 'table':
      return (
        <div className="overflow-x-auto mb-4">
          <table className="w-full text-sm border border-background-200/70 rounded-lg overflow-hidden">
            <thead>
              <tr className="bg-background-200/70">
                {section.headers?.map((h, i) => (
                  <th key={i} className="text-left px-4 py-3 font-semibold text-foreground-800">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {section.rows?.map((row, ri) => (
                <tr key={ri} className={`border-t border-background-200/50 ${ri % 2 === 1 ? 'bg-background-50' : ''}`}>
                  {row.map((cell, ci) => (
                    <td key={ci} className={`px-4 py-3 ${ci === 0 ? 'font-medium text-foreground-800' : ci === row.length - 1 ? 'text-accent-600 font-medium' : 'text-foreground-600'}`}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    case 'cta':
      if (section.ctaInternal) {
        return (
          <div className={`p-6 rounded-lg border text-center mb-6 ${section.ctaStyle === 'secondary' ? 'bg-secondary-100/70 border-secondary-200/50' : 'bg-primary-50 border-primary-200/70'}`}>
            <p className={`text-sm font-semibold mb-3 ${section.ctaStyle === 'secondary' ? 'text-foreground-800' : 'text-primary-700'}`}>
              {section.ctaStyle === 'secondary' ? 'Continue exploring:' : 'Ready to get started?'}
            </p>
            <a
              href={section.ctaUrl}
              target={section.ctaInternal ? undefined : '_blank'}
              rel={section.ctaInternal ? undefined : 'noopener nofollow'}
              className={`inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold rounded-lg transition-colors whitespace-nowrap cursor-pointer ${section.ctaStyle === 'secondary' ? 'bg-secondary-500 text-background-50 hover:bg-secondary-600' : 'bg-primary-500 text-background-50 hover:bg-primary-600'}`}
            >
              {section.ctaInternal ? <i className="ri-arrow-right-line"></i> : <i className="ri-external-link-line"></i>}
              {section.ctaText}
            </a>
          </div>
        );
      }
      return (
        <div className={`p-6 rounded-lg border text-center mb-6 ${section.ctaStyle === 'secondary' ? 'bg-secondary-100/70 border-secondary-200/50' : 'bg-primary-50 border-primary-200/70'}`}>
          <p className={`text-sm font-semibold mb-3 ${section.ctaStyle === 'secondary' ? 'text-foreground-800' : 'text-primary-700'}`}>
            {section.ctaStyle === 'secondary' ? 'Continue exploring:' : 'Ready to get started?'}
          </p>
          <a
            href={section.ctaUrl}
            target="_blank"
            rel="noopener nofollow"
            className={`inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold rounded-lg transition-colors whitespace-nowrap cursor-pointer ${section.ctaStyle === 'secondary' ? 'bg-secondary-500 text-background-50 hover:bg-secondary-600' : 'bg-primary-500 text-background-50 hover:bg-primary-600'}`}
          >
            <i className="ri-external-link-line"></i>
            {section.ctaText}
          </a>
        </div>
      );

    case 'faq':
      if (!section.items || section.items.length === 0) return null;
      const faqItems: { q: string; a: string }[] = [];
      for (let i = 0; i < section.items.length; i += 2) {
        faqItems.push({ q: section.items[i], a: section.items[i + 1] || '' });
      }
      return (
        <div className="space-y-4 mb-6">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4 mt-10">
            Frequently Asked Questions
          </h2>
          {faqItems.map((faq, i) => (
            <div key={i} className="p-5 bg-background-100 rounded-lg border border-background-200/70">
              <h3 className="text-base font-semibold text-foreground-900 mb-2">{faq.q}</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
            </div>
          ))}
        </div>
      );

    case 'image':
      return (
        <div className="w-full h-48 md:h-72 rounded-lg overflow-hidden mb-4">
          <img
            src={section.imageUrl}
            alt={section.imageAlt || ''}
            className="w-full h-full object-cover rounded-lg"
          />
        </div>
      );

    default:
      return null;
  }
}

export default function ArticleLayout({ article }: ArticleLayoutProps) {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);

  const faqSections = article.sections.filter((s) => s.type === 'faq');
  const faqSchemaEntities = faqSections.flatMap((s) => {
    if (!s.items) return [];
    const entities: { '@type': string; name: string; acceptedAnswer: { '@type': string; text: string } }[] = [];
    for (let i = 0; i < s.items.length; i += 2) {
      entities.push({
        '@type': 'Question',
        name: s.items[i],
        acceptedAnswer: {
          '@type': 'Answer',
          text: s.items[i + 1] || '',
        },
      });
    }
    return entities;
  });

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <article className="pt-24 pb-16">
          <header className="bg-background-100 py-12 md:py-16">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <div className="flex items-center gap-2 mb-4">
                <a href="/" className="text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-arrow-left-line mr-1"></i>Home
                </a>
                <span className="text-foreground-300">/</span>
                <span className="text-sm text-foreground-500">{article.category}</span>
              </div>
              <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  Updated August 2026
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  {article.readTime}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  By Sugargoo Spreadsheet Team
                </span>
              </div>
            </div>
          </header>

          <div className="max-w-4xl mx-auto px-4 md:px-6 -mt-8 relative z-10">
            <div className="w-full h-64 md:h-96 rounded-lg overflow-hidden">
              <img
                src={article.heroImageUrl}
                alt={article.heroImageAlt}
                title={article.title}
                className="w-full h-full object-cover rounded-lg"
              />
            </div>
          </div>

          <div className="max-w-3xl mx-auto px-4 md:px-6 mt-10">
            <div className="space-y-1">
              {article.sections.map((section, idx) => (
                <SectionRenderer key={idx} section={section} />
              ))}
            </div>

            <div className="mt-12 p-8 bg-primary-50 rounded-lg border border-primary-200/70 text-center">
              <h3 className="text-xl font-heading font-bold text-foreground-950 mb-3">
                Continue Your Shopping Journey
              </h3>
              <p className="text-sm text-foreground-600 mb-5 max-w-md mx-auto leading-relaxed">
                Explore thousands of curated products across 14 categories on the most comprehensive Sugargoo Spreadsheet platform.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="w-full sm:w-auto px-7 py-3 text-sm font-semibold bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-shopping-bag-line mr-2"></i>
                  Sugargoo Spreadsheet
                </a>
                <a
                  href="/"
                  className="w-full sm:w-auto px-7 py-3 text-sm font-semibold text-foreground-700 border border-background-200/70 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-home-line mr-2"></i>
                  Back to Homepage
                </a>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.metaDescription,
            author: { '@type': 'Organization', name: 'Sugargoo Spreadsheet' },
            datePublished: '2026-08-01',
            dateModified: '2026-08-04',
            publisher: { '@type': 'Organization', name: 'Sugargoo Spreadsheet' },
          }),
        }}
      />
      {faqSchemaEntities.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: faqSchemaEntities,
            }),
          }}
        />
      )}
    </div>
  );
}